"use client";

/**
 * Mantine Modal Stack Component
 * 
 * Manages multiple modals with proper z-index stacking, focus management,
 * and escape key handling. Follows Mantine best practices for modal management.
 * 
 * IMPORTANT: Modal.Stack can only be used with Modal component, NOT with compound components
 * (Modal.Root, Modal.Content, etc.). Use this for simple Modal components only.
 */

import * as React from "react";
import { Modal, useModalsStack } from '@mantine/core';

interface MantineModalStackProps {
  children: React.ReactNode;
  modalIds: string[];
}

export const MantineModalStack: React.FC<MantineModalStackProps> = ({ children, modalIds }) => {
  return (
    <Modal.Stack>
      {children}
    </Modal.Stack>
  );
};

// Note: Use Mantine's useModalsStack directly instead of this wrapper
// export function useMantineModalStack(modalIds: string[]) {
//   return useModalsStack(modalIds);
// }

// Legacy hook for URL-based modal management (not compatible with Modal.Stack)
export const useUrlModalStack = () => {
  const [modals, setModals] = React.useState<Set<string>>(new Set());

  const openModal = React.useCallback((modalId: string) => {
    setModals(prev => new Set([...prev, modalId]));
  }, []);

  const closeModal = React.useCallback((modalId: string) => {
    setModals(prev => {
      const newSet = new Set(prev);
      newSet.delete(modalId);
      return newSet;
    });
  }, []);

  const closeAllModals = React.useCallback(() => {
    setModals(new Set());
  }, []);

  const isModalOpen = React.useCallback((modalId: string) => {
    return modals.has(modalId);
  }, [modals]);

  return {
    modals: Array.from(modals),
    openModal,
    closeModal,
    closeAllModals,
    isModalOpen,
    hasModals: modals.size > 0
  };
};